import React, { useState, useMemo } from 'react';
import { 
  Search, 
  X, 
  Clock, 
  ChevronDown, 
  ChevronRight,
  Sparkles,
  Trash2,
  HelpCircle,
  RefreshCw,
  MapPin
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Separator } from '@/components/ui/separator';
import { cn } from '@flowstate/utils';
import { useQuickActions } from '../hooks/useQuickActions';
import { quickActionsService } from "@flowstate/quick-actions";
import type { QuickAction, SystemState } from '../types/quickActions';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useContextDetection } from '@/hooks/useContextDetection';

interface QuickActionsPanelProps {
  systemState?: SystemState;
  onActionExecute: (prompt: string) => void;
  onClose?: () => void;
}

export function QuickActionsPanel({ 
  systemState, 
  onActionExecute,
  onClose 
}: QuickActionsPanelProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDomain, setSelectedDomain] = useState<string | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<string[]>(['diagnostic', 'navigation']);

  const { 
    actions, 
    groupedActions, 
    searchActions, 
    executeAction,
    recentActions,
    clearRecent,
    refreshActions,
    isLoading 
  } = useQuickActions({ systemState });

  // Get current context for domain indicator
  const { currentContext } = useContextDetection();
  const currentDomain = currentContext?.route?.split('/').filter(Boolean)[0] || 'general';

  // Get unique domains from all actions
  const availableDomains = useMemo(() => {
    return quickActionsService.getUniqueDomains(actions);
  }, [actions]);

  // Get filtered actions based on search and domain
  const filteredActions = useMemo(() => {
    let result = actions;
    if (searchQuery.trim()) {
      result = searchActions(searchQuery);
    }
    if (selectedDomain) {
      result = quickActionsService.filterByDomain(result, selectedDomain);
    }
    return result;
  }, [searchQuery, selectedDomain, actions, searchActions]);

  // Filter out any actions with invalid icons
  const validActions = useMemo(() => {
    return filteredActions.filter(action => action && action.icon);
  }, [filteredActions]);

  // Filter recent actions to ensure valid icons
  const validRecentActions = useMemo(() => {
    return recentActions.filter(action => action && action.icon);
  }, [recentActions]);

  // Get filtered grouped actions based on search and domain
  const filteredGroupedActions = useMemo(() => {
    if (!searchQuery.trim() && !selectedDomain) {return groupedActions;}
    
    return groupedActions
      .map(group => ({
        ...group,
        actions: group.actions.filter(action => 
          validActions.some(fa => fa.id === action.id)
        )
      }))
      .filter(group => group.actions.length > 0);
  }, [searchQuery, selectedDomain, groupedActions, validActions]);

  // Toggle category expansion
  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => 
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  // Handle action click — execute and auto-close panel to speed up user workflow
  const handleActionClick = (action: QuickAction) => {
    executeAction(action, onActionExecute);
    onClose?.();
  };

  // Render individual action button
  const renderAction = (action: QuickAction) => {
    const Icon = action.icon || HelpCircle;
    
    return (
      <Tooltip key={action.id} delayDuration={300}>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "w-full justify-start gap-2 h-auto py-2.5 px-3",
              "hover:bg-secondary/80 transition-colors",
              "text-left"
            )}
            onClick={() => handleActionClick(action)}
          >
            <Icon className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium truncate">{action.label}</span>
                {action.domain && (
                  <Badge variant="outline" className="h-4 text-[9px] px-1 shrink-0 font-normal">
                    {action.domain}
                  </Badge>
                )}
                {action.isNew && (
                  <Badge variant="secondary" className="h-5 text-[10px] px-1.5">
                    NEW
                  </Badge>
                )}
              </div>
              {action.description && (
                <p className="text-xs text-muted-foreground truncate mt-0.5">
                  {action.description}
                </p>
              )}
            </div>
            {action.estimatedTime && (
              <span className="text-[10px] text-muted-foreground flex-shrink-0 ml-2">
                {action.estimatedTime}
              </span>
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="left" className="max-w-xs">
          <div className="space-y-1">
            <p className="font-semibold">{action.label}</p>
            <p className="text-xs text-muted-foreground">{action.description}</p>
            {action.keywords.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {action.keywords.slice(0, 4).map(keyword => (
                  <Badge key={keyword} variant="outline" className="text-[10px] px-1.5 py-0">
                    {keyword}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    );
  };

  return (
    <TooltipProvider>
      <div className="flex flex-col h-full bg-background/95 backdrop-blur-sm border-r">
        {/* Header */}
        <div className="p-3 border-b bg-secondary/30 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <h3 className="font-semibold text-sm">Quick Actions</h3>
            {!isLoading && (
              <Badge variant="secondary" className="h-5 text-[10px] px-1.5">
                {validActions.length}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Tooltip delayDuration={300}>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={refreshActions}
                  disabled={isLoading}
                >
                  <RefreshCw className={cn("h-3.5 w-3.5", isLoading && "animate-spin")} />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p className="text-xs">Refresh actions</p>
              </TooltipContent>
            </Tooltip>
            {onClose && (
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={onClose}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>

        {/* Context indicator */}
        <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
          <MapPin className="h-3 w-3 text-primary" />
          <span>Context: <span className="font-medium text-foreground capitalize">{currentDomain}</span></span>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Search actions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-8 pl-8 pr-8 text-xs"
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-0.5 top-1/2 -translate-y-1/2 h-7 w-7"
              onClick={() => setSearchQuery('')}
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>

        {/* Domain filter chips */}
        {availableDomains.length > 1 && (
          <div className="flex flex-wrap gap-1">
            <Button
              variant={selectedDomain === null ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedDomain(null)}
              className="h-6 text-[10px] px-2"
            >
              All
            </Button>
            {availableDomains.map(domain => (
              <Button
                key={domain}
                variant={selectedDomain === domain ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedDomain(domain)}
                className="h-6 text-[10px] px-2 capitalize"
              >
                {domain}
              </Button>
            ))}
          </div>
        )}
      </div>

      {/* Content */}
      <ScrollArea className="flex-1">
        <div className="p-2 space-y-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-muted-foreground">Loading actions...</div>
            </div>
          ) : validActions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
              <Search className="h-8 w-8 text-muted-foreground/50 mb-2" />
              <p className="text-sm font-medium">No actions found</p>
              <p className="text-xs text-muted-foreground mt-1">
                {selectedDomain 
                  ? `No actions in "${selectedDomain}" domain. Try clearing the filter.`
                  : 'Try a different search term'}
              </p>
              {selectedDomain && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-2 text-xs"
                  onClick={() => setSelectedDomain(null)}
                >
                  Clear domain filter
                </Button>
              )}
            </div>
          ) : (
            <>
              {/* Recent Actions */}
              {!searchQuery && !selectedDomain && validRecentActions.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center justify-between px-2 mb-2">
                    <div className="flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        Recent
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-[10px]"
                      onClick={clearRecent}
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Clear
                    </Button>
                  </div>
                  <div className="space-y-0.5">
                    {validRecentActions.map(action => renderAction(action))}
                  </div>
                  <Separator className="my-3" />
                </div>
              )}

              {/* Grouped Actions */}
              {searchQuery || selectedDomain ? (
                // Flat list when searching or filtering by domain
                <div className="space-y-0.5">
                  {validActions.map(action => renderAction(action))}
                </div>
              ) : (
                // Categorized list when not searching
                <div className="space-y-2">
                  {filteredGroupedActions.map((group) => {
                    const Icon = group.icon || HelpCircle;
                    const isExpanded = expandedCategories.includes(group.category);

                    return (
                      <Collapsible
                        key={group.category}
                        open={isExpanded}
                        onOpenChange={() => toggleCategory(group.category)}
                      >
                        <CollapsibleTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full justify-between h-auto py-2 px-2 hover:bg-secondary/50"
                          >
                            <div className="flex items-center gap-2">
                              <Icon className="h-4 w-4 text-primary" />
                              <div className="text-left">
                                <div className="text-xs font-semibold">{group.label}</div>
                                <div className="text-[10px] text-muted-foreground">
                                  {group.actions.length} {group.actions.length === 1 ? 'action' : 'actions'}
                                </div>
                              </div>
                            </div>
                            {isExpanded ? (
                              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                            )}
                          </Button>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="space-y-0.5 mt-1 ml-2">
                          {group.actions.map(action => renderAction(action))}
                        </CollapsibleContent>
                      </Collapsible>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </ScrollArea>

      {/* Footer hint */}
      {!isLoading && validActions.length > 0 && (
        <div className="p-2 border-t bg-secondary/20">
          <p className="text-[10px] text-muted-foreground text-center">
            Click any action to execute immediately
          </p>
        </div>
      )}
      </div>
    </TooltipProvider>
  );
}
